import { z } from 'zod';
import { EVENT_KINDS, LABEL_COLORS, RECURRENCES } from '../domain';

const dt = z.iso.datetime({ offset: true });

export const eventInput = z
  .object({
    title: z.string().trim().min(1, 'Dê um título').max(200),
    kind: z.enum(EVENT_KINDS).default('meeting'),
    description: z
      .string()
      .trim()
      .max(10_000)
      .optional()
      .transform((v) => v || null),
    startsAt: dt,
    endsAt: dt,
    allDay: z.boolean().default(false),
    location: z
      .string()
      .trim()
      .max(300)
      .optional()
      .transform((v) => v || null),
    url: z
      .union([z.url(), z.literal('')])
      .optional()
      .transform((v) => v || null),
    projectId: z
      .uuid()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    clientId: z
      .uuid()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    taskId: z
      .uuid()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    color: z
      .enum(LABEL_COLORS)
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    recurrence: z.enum(RECURRENCES).default('none'),
    recurrenceUntil: z.iso
      .date()
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    visibility: z.enum(['internal', 'client']).default('internal'),
    attendeeIds: z.array(z.uuid()).max(50).default([]),
  })
  .refine((v) => new Date(v.endsAt) >= new Date(v.startsAt), {
    path: ['endsAt'],
    message: 'O fim precisa ser depois do início',
  });
export type EventInput = z.input<typeof eventInput>;

export const eventTimeInput = z.object({ startsAt: dt, endsAt: dt });

export const calendarQuery = z.object({
  from: dt,
  to: dt,
  projectId: z.uuid().optional(),
  clientId: z.uuid().optional(),
  userId: z.union([z.uuid(), z.literal('me')]).optional(),
  /** Qual prazo das tarefas mostrar. */
  due: z.enum(['internal', 'client', 'both']).default('internal'),
  kinds: z.string().max(200).optional(),
});

const person = z.object({ id: z.uuid(), name: z.string(), avatarUrl: z.string().nullable() });

/** Item do calendário: evento (ocorrência) ou prazo de tarefa. */
export const calendarItemDto = z.object({
  /** Único por ocorrência: `event:<id>:<data>` ou `task:<id>:internal|client|start`. */
  key: z.string(),
  type: z.enum(['event', 'task']),
  id: z.uuid(),
  title: z.string(),
  kind: z.string(),
  start: z.string(),
  end: z.string(),
  allDay: z.boolean(),
  color: z.enum(LABEL_COLORS),
  project: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  client: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  people: z.array(person),
  /** Tarefa: etapa e se está concluída. Evento: local/link. */
  status: z.string().nullable(),
  done: z.boolean(),
  location: z.string().nullable(),
  url: z.string().nullable(),
  recurring: z.boolean(),
  editable: z.boolean(),
  /** Para tarefas: qual campo este item representa (para remarcar ao arrastar). */
  field: z.enum(['internalDueAt', 'clientDueAt', 'event']),
});
export type CalendarItemDto = z.infer<typeof calendarItemDto>;

export const eventDto = z.object({
  id: z.uuid(),
  title: z.string(),
  kind: z.enum(EVENT_KINDS),
  description: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  allDay: z.boolean(),
  location: z.string().nullable(),
  url: z.string().nullable(),
  project: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  client: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  taskId: z.uuid().nullable(),
  color: z.enum(LABEL_COLORS).nullable(),
  recurrence: z.enum(RECURRENCES),
  recurrenceUntil: z.string().nullable(),
  visibility: z.enum(['internal', 'client']),
  attendees: z.array(person.extend({ response: z.enum(['pending', 'accepted', 'declined']) })),
  createdBy: z.string().nullable(),
  canEdit: z.boolean(),
});
export type EventDto = z.infer<typeof eventDto>;
